import { useRef } from 'react'

/**
 * Drag-to-scroll for the rail's horizontal strips. Plant-floor touch panels
 * pan natively (overflow-x: auto containers respond to swipe), but with a
 * mouse the only affordance was the thin scrollbar: these handlers let the
 * pointer grab anywhere on a strip and drag it. A click that follows a real
 * drag is swallowed in the capture phase so fold/unfold chips don't toggle
 * accidentally at the end of a swipe. Touch/pen pointers are ignored — native
 * panning already handles them (and must not be hijacked).
 *
 * Spread the returned handlers on every scrollable strip:
 *   <div className='ppx-scroll' {...dragScroll}>
 */
export const useDragScroll = () => {
  const dragState = useRef({ active: false, startX: 0, startScrollLeft: 0, dragged: false })

  const onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) {
      return
    }
    dragState.current = {
      active: true,
      startX: event.clientX,
      startScrollLeft: event.currentTarget.scrollLeft,
      dragged: false
    }
  }

  const onPointerMove = (event) => {
    const state = dragState.current
    if (!state.active) {
      return
    }
    const deltaX = event.clientX - state.startX
    if (!state.dragged && Math.abs(deltaX) > 4) {
      state.dragged = true
      event.currentTarget.setPointerCapture?.(event.pointerId)
    }
    if (state.dragged) {
      event.currentTarget.scrollLeft = state.startScrollLeft - deltaX
    }
  }

  const endDrag = () => {
    dragState.current.active = false
    // The click after a drag fires synchronously before this timeout, so
    // onClickCapture still sees dragged=true and swallows it; afterwards the
    // flag clears even when the drag ended outside any clickable element.
    setTimeout(() => { dragState.current.dragged = false }, 0)
  }

  const onClickCapture = (event) => {
    if (dragState.current.dragged) {
      event.preventDefault()
      event.stopPropagation()
      dragState.current.dragged = false
    }
  }

  return { onPointerDown, onPointerMove, onPointerUp: endDrag, onPointerLeave: endDrag, onClickCapture }
}
